import { useEffect, useState } from "react";
import { Alert, Container, Paper, Snackbar, Tab, Tabs, ThemeProvider, Typography } from "@mui/material";
import { temaJornada } from "./temaJornada";
import ConfigUtils from "../../utils/Config";
import IAlert from "../../types/Alert";
import ConfiguracionJornada from "./ConfiguracionJornada";
import MiJornada from "./MiJornada";
import MisFichajes from "./MisFichajes";
import HoyJornada from "./HoyJornada";
import RegistrosJornada from "./RegistrosJornada";

// Página de Registro de jornada. Las pestañas dependen de quién entra: quien ficha tiene Mi jornada (la primera) y Mis
// fichajes; quien ya no ficha pero tiene registros, solo Mis fichajes, en lectura; el admin, además, Hoy y Registros
// (detrás de Mi jornada) y Configuración.
const RegistroJornada = () => {
    // Se vuelve a leer cuando el admin cambia si él mismo ficha (evento popstate, como en la barra)
    const [registro, setRegistro] = useState(ConfigUtils.getRegistroJornada());
    const esAdmin = registro?.esAdmin === true;
    const ficha = registro?.puedeFichar === true;
    const tieneRegistros = registro?.tieneRegistros === true;
    const pestanas = [
        ...(ficha ? ['miJornada'] : []),
        ...(esAdmin ? ['hoy', 'registros'] : []),
        ...(ficha || tieneRegistros ? ['misFichajes'] : []),
        ...(esAdmin ? ['configuracion'] : []),
    ];
    const [pestana, setPestana] = useState(pestanas[0] ?? '');

    useEffect(() => {
        const releer = () => setRegistro(ConfigUtils.getRegistroJornada());
        window.addEventListener('popstate', releer);
        return () => window.removeEventListener('popstate', releer);
    }, []);

    // Si la pestaña abierta deja de estar (por ejemplo, el admin deja de fichar), pasa a la primera que quede
    const disponibles = pestanas.join('|');
    useEffect(() => {
        if (!pestanas.includes(pestana)) setPestana(pestanas[0] ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disponibles, pestana]);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });

    const handleCloseAlert = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const NOMBRES: Record<string, string> = { miJornada: 'Mi jornada', hoy: 'Hoy', registros: 'Registros', misFichajes: 'Mis fichajes', configuracion: 'Configuración' };

    const contenido = () => {
        if (pestanas.length === 0) {
            return <Typography variant="body1">El registro de jornada no está activo para ti. Si crees que es un error, habla con el administrador de tu clínica.</Typography>;
        }
        return (
            <>
                {/* En el móvil, pestañas compactas para que la seleccionada se vea entera */}
                {pestanas.length > 1 && (
                    <Tabs value={pestanas.includes(pestana) ? pestana : pestanas[0]} onChange={(_e, v: string) => setPestana(v)} variant="scrollable" allowScrollButtonsMobile
                        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minWidth: { xs: 0, sm: 90 }, px: { xs: 1.5, sm: 2 } } }}>
                        {pestanas.map(p => <Tab key={p} label={NOMBRES[p]} value={p} />)}
                    </Tabs>
                )}
                {pestana === 'miJornada' && <MiJornada setAlert={setAlert} />}
                {pestana === 'hoy' && <HoyJornada setAlert={setAlert} />}
                {pestana === 'registros' && <RegistrosJornada setAlert={setAlert} />}
                {pestana === 'misFichajes' && <MisFichajes setAlert={setAlert} soloLectura={!ficha} />}
                {pestana === 'configuracion' && <ConfiguracionJornada setAlert={setAlert} />}
            </>
        );
    };

    // Formas del design system de Zyra solo en esta sección (también en sus diálogos)
    return (
        <ThemeProvider theme={temaJornada}>
            <Container>
                <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, md: 4 }, mb: 3 }}>
                    <Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 600 }}>Registro de jornada</Typography>
                    {contenido()}
                </Paper>
                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default RegistroJornada;
